import React, { useCallback } from "react";

import { makeToast } from "../Toast";
import { buttonReducerErrorMessage } from "./buttonReducerErrorMessage";

export function useButtonReducer(reducer: unknown, loading: unknown, disabled: unknown) {
	const [value, setValue] = React.useState<unknown>(() => (reducer === undefined ? 0 : null));
	const [isLoading, setIsLoading] = React.useState<boolean>(loading === true);
	const [isDisabledByReducer, setIsDisabledByReducer] = React.useState(false);

	const reducerCallback = useCallback(
		async (value: unknown) => {
			const reducerOrDefault =
				reducer && typeof reducer === "function" ? reducer : (value: number) => value + 1;

			//
			// Catch and report errors coming out of `reducerCallback` if it's not a promise.
			//

			let maybePromise: unknown;
			try {
				maybePromise = reducerOrDefault(value);
			} catch (e) {
				const message = buttonReducerErrorMessage(e);
				console.error(e, message);
				makeToast({
					message,
					variant: "error",
					timer: "dismissible",
				});

				return;
			}

			//
			// If `reducerCallback` returns a `Promise`, show loading state while it resolves, and
			// report errors as a toast.
			//

			if (
				!maybePromise ||
				typeof maybePromise !== "object" ||
				!("then" in maybePromise) ||
				typeof maybePromise.then !== "function"
			) {
				// Whatever non-promise value was returned, just use it as the new state.
				setValue(maybePromise);
				return;
			}

			// IMPORTANT: Do this *AFTER* checking return value is a promise, otherwise we
			// get a flickering "loading" state for any button click.
			setIsLoading(true);
			setIsDisabledByReducer(true);

			try {
				setValue(await (maybePromise as Promise<unknown>));
				makeToast({
					message: "Action completed successfully.",
					variant: "success",
					timer: "timed-dismissible",
				});
			} catch (e) {
				const message = buttonReducerErrorMessage(e);
				console.error(e, message);
				makeToast({
					message,
					variant: "error",
					timer: "dismissible",
				});
			} finally {
				setIsLoading(false);
				setIsDisabledByReducer(false);
			}
		},
		[reducer]
	);

	return { value, isLoading, isDisabled: isDisabledByReducer || !!disabled, reducerCallback };
}
